import { complianceBreachApi, eplApi, expertApi, getProjectSuggestions, projectApi, segmentApi } from '@atheneum/api'

import { isCancellationError, showDialog } from '@components/Dialog'
import { addQueryParams, formatDateToDate } from '@atheneum/api/dist/lib'
import _ from 'lodash'
import get from 'lodash/get'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { extendObservable, toJS } from 'mobx'
import { sendMetric } from '@atheneum/api/dist/lib'

class ExpertToProjectFormStore {
  constructor(
    staticData,
    modalStore,
    experts,
    growlStore,
    activeProjectStore,
    isCopy,
    expertSearchStore
  ) {
    extendObservable(this, {
      staticData,
      modalStore,
      expertSearchStore,
      growlStore,
      activeProjectStore,
      loading: false,
      expertList: [],
      progress: {
        submitting: false,
        total: null,
        valid: null,
        processed: 0
      },
      expertListErrors: [],
      expertListInviteErrors: [],
      expertListWarnings: [],
      projectId: null,
      project: null,
      segments: [],
      eplsData: {},
      isCopy
    })

    this.loadExpertsData(experts)
  }

  updateInviteErrors = errorList => {
    this.expertListInviteErrors.replace(errorList)
  }

  updateErrors = errorList => {
    this.expertListErrors.replace(errorList)
  }

  updateWarnings = warningList => {
    this.expertListWarnings.replace(warningList)
  }

  updateAllErrors = errorList => {
    this.expertListErrors.replace(errorList)
    this.expertListInviteErrors.replace(errorList)
  }

  async loadExpertsData(experts) {
    this.loading = true

    const pdlList = experts.filter(e => typeof e.id === 'string')
    const missingIds = []
    const expertData = await expertApi.getExpertsForProject(
      experts
        // eslint-disable-next-line
        .filter(e => {
          if (Number(e.id)) {
            return Number(e.id)
          } else {
            missingIds.push(e.id)
          }
        })
        .map(e => e.id)
    )

    for (const pdl of pdlList) {
      try {
        const pdlExpert = await expertApi.getPDLExpert(pdl.id)
        const checked = await expertApi.checkExpert({
          emails: pdlExpert.emails
            ? pdlExpert.emails.map(e => e.email)
            : undefined,
          fullName: pdlExpert.fullName,
          linkedinVanityName: pdlExpert.linkedinVanityName,
          link: pdlExpert.linkedinVanityName
            ? `https://www.linkedin.com/in/${pdlExpert.linkedinVanityName}`
            : undefined
        })

        if (checked.id) {
          const foundExpert = await expertApi.getOneById(checked.id)
          expertData.push(foundExpert)
        } else {
          expertData.push(pdlExpert)
        }
      } catch (error) {
        return this.growlStore.showError(error)
      }
    }

    const expertList = expertData.map(expert => {
      const expertData = pick(expert, [
        'expert',
        'fullName',
        'id',
        'email',
        'title',
        'languages'
      ])

      expertData.experiences = get(expert, 'cv.experiences', []).map(
        experience => {
          const { startDate, endDate } = experience
          const duration = formatDateToDate(startDate, endDate, 'MMM YYYY')
          const positionName = experience.position
            ? experience.position.name || experience.position
            : 'Not specified'
          const companyName = experience.company
            ? experience.company.name || experience.company
            : 'Not specified'
          experience.label = `${positionName}, ${companyName} (${duration})`
          return experience
        }
      )

      expertData.position =
        expertData.experiences && expertData.experiences.length
          ? expertData.experiences[0]
          : null

      return expertData
    })

    const baseList = expertList
    const res = await expertApi.checkSherlockList(
      baseList.map(expert => ({
        id: expert.id,
        email: expert.email,
        position: expert.position,
        experiences: _.uniq(
          expert.experiences
            .filter(e => e.current)
            .map(exp => exp.company.name || exp.company)
            .filter(c => c)
        ),
        linkedinVanityName:
          expert.linkedinVanityName || expert.expert.linkedinVanityName
      }))
    )

    const resObj = res.reduce(
      (obj, item) => ({ ...obj, [item.id]: item.sherlock }),
      {}
    )
    const result = baseList.map(expert => ({
      ...expert,
      sherlock: resObj[expert.id]
    }))

    result.forEach(e => {
      e.sherlock = res.find(r => r.id === e.id)?.sherlock
    })

    this.expertList.replace(result)

    const { activeProjectId } = this.activeProjectStore
    if (activeProjectId) {
      const project = await projectApi.getOne(activeProjectId)
      if (project) {
        this.projectId = activeProjectId
        this.project = project
        const segments = await segmentApi.getByProject(activeProjectId)
        this.segments.replace(segments)
      }

      await this.revalidate({
        projectId: activeProjectId,
        expertList: this.expertList.filter(e => Number(e.id))
      })
    }

    this.loading = false
  }

  handleProjectAutocomplete = input => {
    if (!input) return Promise.resolve([])

    return getProjectSuggestions(input)
      .then(projects => {
        return projects
      })
      .catch(err => {
        console.error(err)
      })
  }

  revalidate = async ({ expertList, projectId }) => {
    const expertIds = expertList.map(({ expert }) => expert.userId || expert.id)

    if (expertList.filter(e => Number(e.id)).length) {
      const response = await eplApi.bulkValidate({
        projectId,
        expertIds: expertIds.filter(e => e)
      })
      const errors = []
      const warnings = []
      if (response && response.length > 0) {
        for (const eplStatus in response) {
          if (response[eplStatus].warning) warnings.push(response[eplStatus])
          else errors.push(response[eplStatus])
        }
      }
      this.expertListErrors.replace(errors)
      this.expertListWarnings.replace(warnings)
    }
  }

  breach = async (values, comments, projectName, userId) => {
    try {
      for (const expert of values) {
        const breach = await complianceBreachApi.breach({
          ruleId: expert.sherlock?.reason?.id,
          userId,
          action: `Add expert to project: ${projectName}`,
          rule: expert.sherlock?.display_reason,
          expertId: expert.id,
          confirmed: null
        })

        await complianceBreachApi.comment({
          userId,
          breachId: breach?.id,
          comment: comments[expert.pdlId ? expert.pdlId : expert.id]
        })
      }
    } catch (error) {
      throw new Error(error)
    }
  }

  onProjectChange = async (project, form) => {
    form.setFieldValue('project', project, false)

    if (!project) {
      form.setFieldValue('projectId', '', false)
      form.setFieldTouched('projectId', true)
    } else {
      form.setFieldValue('projectId', project.id, false)
      form.setFieldTouched('projectId', true)

      segmentApi.getByProject(project.id).then(segments => {
        this.segments.replace(segments)
      })

      await this.revalidate({ ...form.values, projectId: project.id })

      // make sure to tag all expert position fields as touched
      const expertListLength = form.values.expertList
        ? form.values.expertList.length
        : 0
      for (let i = 0; i < expertListLength; i++) {
        form.setFieldTouched(`expertList[${i}].position`, true)
      }
    }
  }

  getInitialValues() {
    if (this.eplsData && this.eplsData.expertList) {
      return {
        ...this.eplsData,
        expertList: toJS(this.eplsData.expertList),
        replyStatusId: null
      }
    } else {
      let segment
      if (this.segments.length) {
        segment = this.segments[0]
      } else {
        segment = {
          id: null
        }
      }
      return {
        projectId: this.projectId,
        project: this.project,
        segment,
        expertList: toJS(this.expertList),
        replyStatusId: null
      }
    }
  }

  validateExpertEmails = expertList => {
    const errors = expertList.reduce((errors, { id, email }) => {
      if (!email) {
        errors.push({
          expertId: id,
          error: `Expert has no email and thus can't be invited.`
        })
      }
      return errors
    }, [])
    this.expertListInviteErrors.replace(errors)
  }

  openInvitationModal = (
    senderIds,
    segmentId,
    history,
    formValues,
    projectName,
    resetPinned
  ) => {
    try {
      const hasNonColdExperts = formValues.expertList.filter(expert => expert.expert.expertStatusId !== 5)
      const expertIds = formValues.expertList.map(({ expert }) => expert.userId)

      const loadTemplateUrl = addQueryParams(
        '/api/expert-project-link/expert-invite-template',
        { expertIds, segmentId }
      )

      const user = JSON.parse(window.localStorage.getItem('ngStorage-user'))
      const props = {
        group: hasNonColdExperts.length ? 'expert_invite' : 'cold_expert_invite',
        loadTemplateUrl,
        onSend: async templateData => {
          if (
            formValues.expertList.filter(e => e.sherlock && !e.sherlock.allowed)
              .length > 0
          ) {
            await this.breach(
              formValues.expertList.filter(
                e => e.sherlock && !e.sherlock.allowed
              ),
              formValues.comments,
              projectName,
              user.id
            )
          }
          if (resetPinned) {
            await resetPinned()
          }
          return this.createEPLs(formValues.expertList, history, templateData)
        },
        progress: this.progress,
        senderIds
      }

      this.modalStore.show({
        type: 'EMAIL_TEMPLATE_SEND',
        props
      })
    } catch (error) {
      return error
    }

  }

  buildExpertList = epls => {
    return epls.map(epl => epl.expert.fullName).join(',<br />')
  }

  createEPLs = async (experts, history, templateData, addOnly = false) => {
    this.progress.submitting = true

    const startTime = new Date().getTime()

    const invalidIds = this.expertListErrors
      .concat(addOnly ? [] : this.expertListInviteErrors)
      .map(e => e.expertId)

    const eplsData = experts
      .filter(e => !invalidIds.includes(e.id))
      .map(expert => ({
        expertId: expert.id,
        cvId: expert.position.cvId,
        experienceId: expert.position.id,
        ...omit(this.eplsData, ['expertList', 'project']),
        replyStatusId: this.eplsData.replyStatusId
      }))

    this.progress.total = this.eplsData.length
    this.progress.valid = eplsData.length
    this.progress.processed = 0

    const epls = []

    for (const chunkedExperts of _.chunk(eplsData, 20)) {
      try {
        const processedEpls = await eplApi.bulkCreate({
          expertProjectLinks: chunkedExperts,
          searchServiceRequestId: this.expertSearchStore.searchServiceRequestId,
          templateData
        })
        this.progress.processed += chunkedExperts.length
        epls.push(...processedEpls)
      } catch (err) {
        this.progress.failed += chunkedExperts.length
        this.modalStore.close()
        this.growlStore.showError(err)
        sendMetric('bulkCreateFailed', window.location.hostname, {})
      }
    }

    const duration = new Date().getTime() - startTime
    sendMetric('bulkCreateEplTime', window.location.hostname, {}, duration)
    sendMetric('bulkCreateEplRate', window.location.hostname, {}, duration / epls.length)
    this.modalStore.close()

    // Show result
    const titleAction = templateData ? 'invited' : 'added'
    const titleBase =
      epls.length > 1
        ? `Experts have been ${titleAction} to the project`
        : `Expert has been ${titleAction} to the project`
    const title = titleBase + ' ' + this.eplsData.project.projectName
    const groupedEpls = epls.reduce((acc, epl) => {
      if (epl.failed) {
        acc.failed.push(epl)
      } else {
        acc.created.push(epl)
      }
      return acc
    }, {
      failed: [],
      created: []
    })

    return showDialog({
      title,
      type: 'success',
      html: `
        <p>${this.buildExpertList(groupedEpls.created)}</p>
        ${
          groupedEpls.failed.length > 0
            ? `<div>
              <div class="swal2-icon swal2-error swal2-icon-show">
                <span class="swal2-x-mark">
                  <span class="swal2-x-mark-line-left"></span>
                  <span class="swal2-x-mark-line-right"></span>
                </span>
              </div>
              <h2 class="swal2-title">Failed to create EPL's for the following experts</h2>
              <p style="color: #FF5858">${this.buildExpertList(
                groupedEpls.failed
              )}</p>
            </div>`
            : ''
        }
      `,
      confirmButtonText: 'Go to project'
    })
      .then(() => {
        history.push('/project/' + this.eplsData.project.id)
      })
      .catch(error => {
        if (isCancellationError(error)) return
        this.growlStore.showError(error)
      })
  }
}

export default ExpertToProjectFormStore
